import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SyncStatus } from "@/components/SyncStatus";
import { EuroTable } from "@/components/euro/EuroTable";
import { EuroGames } from "@/components/euro/EuroGames";
import { EuroClubs } from "@/components/euro/EuroClubs";
import { useEuroLive } from "@/hooks/useEuroLive";
import "@/components/standings/standings.css";
import "@/components/euro/euro.css";

const COMPS = [["E", "EuroLeague"], ["U", "EuroCup"]];
const updated = (iso) => iso && new Date(iso).toLocaleString("en-GB", { timeZone: "Europe/Athens", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export default function Europe() {
  const { comp } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("standings");
  const live = useEuroLive();
  const { user } = useAuth();
  const [syncing, setSyncing] = useState(false);

  const refreshNow = async () => {
    setSyncing(true);
    const before = data?.updated_at;
    try {
      await api.post("/euro/sync");
      toast.success("Ξεκίνησε η ανανέωση δεδομένων…");
      for (let i = 0; i < 24; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const r = await api.get(`/euro/${comp}`);
        setData(r.data);
        if (r.data.updated_at && r.data.updated_at !== before) { toast.success("Τα δεδομένα ανανεώθηκαν"); break; }
      }
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSyncing(false);
    }
  };
  const liveGames = data ? data.upcoming.filter((g) => live[g.id]?.live) : [];
  const justFinal = data ? data.upcoming.filter((g) => live[g.id]?.final) : [];

  useEffect(() => {
    if (!comp || !COMPS.some(([c]) => c === comp)) { navigate("/europe/E", { replace: true }); return; }
    setData(null); setError("");
    api.get(`/euro/${comp}`).then((r) => setData(r.data)).catch((e) => setError(errorText(e)));
  }, [comp, navigate]);

  return (
    <div className="ncs-root eu-root" data-testid="europe-page">
      <header className="ncs-heading">
        <div>
          <div className="ncs-eyebrow">EUROPE / {comp === "E" ? "EUROLEAGUE" : "EUROCUP"}</div>
          <h1 data-testid="europe-title">{comp === "E" ? "EUROLEAGUE" : "EUROCUP"} <span>{data?.season ? `${data.season.slice(1)}-${String(Number(data.season.slice(1)) + 1).slice(2)}` : ""}</span></h1>
          <p data-testid="europe-updated">{data ? `Regular Season · Round ${data.round} · Updated ${updated(data.updated_at)} (Athens)` : ""}</p>
          {data && <div style={{ marginTop: 8 }}>
            <SyncStatus lastSync={data.updated_at} onRefresh={refreshNow} busy={syncing} canRefresh={user?.role === "admin"} testid="europe-sync" />
          </div>}
        </div>
        <div className="eu-comp" role="tablist" aria-label="Competition">
          {COMPS.map(([c, label]) => (
            <Link key={c} to={`/europe/${c}`} role="tab" aria-selected={comp === c} className={comp === c ? "active" : ""} data-testid={`europe-comp-${c}`}>{label}</Link>
          ))}
        </div>
      </header>
      <nav className="ncs-tabs" aria-label="Europe view">
        {[["standings", "STANDINGS"], ["teams", "TEAMS"]].map(([k, t]) => (
          <button key={k} type="button" className={tab === k ? "active" : ""} aria-pressed={tab === k} onClick={() => setTab(k)} data-testid={`europe-tab-${k}`}>{t}</button>
        ))}
      </nav>
      {error && <p className="ncs-state" role="alert" data-testid="europe-error">{error}</p>}
      {!data && !error && <p className="ncs-state" role="status" data-testid="europe-loading">Loading {comp === "E" ? "EuroLeague" : "EuroCup"}…</p>}
      {data && tab === "standings" && (
        <div className="ncs-layout">
          <section className="ncs-main">
            {data.groups.map((g) => <EuroTable key={g.name} comp={comp} group={g} single={data.groups.length === 1} />)}
            {!data.groups.length && <p className="ncs-state" data-testid="europe-empty">No games played yet this season.</p>}
            <p className="ncs-footnote">Official {data.name} standings (api-live.euroleague.net). Updated every hour on game days.</p>
          </section>
          <aside className="ncs-sidebar">
            {liveGames.length > 0 && <EuroGames title="LIVE NOW" id="live" comp={comp} games={liveGames} live={live} />}
            <EuroGames title="RECENT RESULTS" id="recent" comp={comp} games={[...justFinal, ...data.recent]} live={live} />
            <EuroGames title="UPCOMING" id="upcoming" comp={comp} games={data.upcoming.filter((g) => !live[g.id]?.live && !live[g.id]?.final)} live={live} />
          </aside>
        </div>
      )}
      {data && tab === "teams" && <EuroClubs comp={comp} clubs={data.clubs} />}
    </div>
  );
}
